<template>
  <main
    id="stock"
    class="page-container stock-list"
    v-if="$route.name === 'listini-category'"
  >
    <div class="table-container-stock">
      <div class="items-toolbar">
        <router-link
          :to="`/home/listini/${category}/add-item`"
          class="primary-btn"
        >
          <i class="fa-solid fa-plus"></i> Nuovo articolo
        </router-link>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- [sc-48920] ricerca e filtri sopra la tabella -->
      <div class="barra-filtri">
        <div class="search-container">
          <input
            type="text"
            v-model="searchTerm"
            @keyup.enter="onSearch"
            placeholder="Cerca codice, nome, serie…"
            class="search-box"
            aria-label="Cerca articoli"
          />
          <button
            v-if="searchTerm"
            @click="onResetSearch"
            class="clear-search-button"
            title="Cancella ricerca"
            type="button"
          >
            <i class="fa-solid fa-times"></i>
          </button>
          <i v-else class="fa-solid fa-magnifying-glass search-icon"></i>
        </div>
        <StatusSelect
          v-if="valoriPresenti(valori.brand) > 1"
          :modelValue="filtri.brand"
          @update:modelValue="(v) => imposta('brand', v)"
          class="filtro-pillola"
          :options="opzioniFiltro(valori.brand, { tutti: 'Tutti i brand' })"
          placeholder-icon="fa-filter"
          searchable
          search-placeholder="Cerca brand…"
          aria-label="Filtra per brand"
        />
        <StatusSelect
          v-if="valoriPresenti(valori.serieCategoria)"
          :modelValue="filtri.serie"
          @update:modelValue="(v) => imposta('serie', v)"
          class="filtro-pillola"
          :options="
            opzioniFiltro(filtri.brand ? valori.serie : valori.serieCategoria, {
              tutti: 'Tutte le serie',
            })
          "
          placeholder-icon="fa-filter"
          searchable
          search-placeholder="Cerca serie…"
          aria-label="Filtra per serie"
        />
        <StatusSelect
          v-if="valoriPresenti(valori.tipologia)"
          :modelValue="filtri.tipologia"
          @update:modelValue="(v) => imposta('tipologia', v)"
          class="filtro-pillola"
          :options="
            opzioniFiltro(valori.tipologia, {
              tutti: 'Tutte le tipologie',
              capitalizza: true,
            })
          "
          placeholder-icon="fa-filter"
          searchable
          search-placeholder="Cerca tipologia…"
          aria-label="Filtra per tipologia"
        />
        <button
          v-if="filtriAttivi(filtri, searchTerm)"
          class="azzera-filtri"
          type="button"
          @click="azzeraFiltri"
        >
          Azzera filtri
        </button>
      </div>
      <div class="tbl-wrap">
        <table class="tbl tbl-elenco item-table">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                :class="col.prio && `col-p${col.prio}`"
              >
                {{ col.label }}
              </th>
              <th style="text-align: left">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in items" :key="index">
              <tr
                class="item-row riga-cliccabile"
                tabindex="0"
                @click="apriDettaglioArticolo($event, item)"
                @keydown.enter="apriDettaglioArticolo($event, item)"
              >
                <td
                  v-for="col in columns"
                  :key="col.key"
                  :class="[
                    { 'td-name': col.truncate },
                    col.prio && `col-p${col.prio}`,
                  ]"
                  :title="col.truncate ? item[col.key] ?? '' : undefined"
                >
                  <span v-if="col.format === 'price'">{{
                    formatPriceToPrint(item[col.key])
                  }}</span>
                  <span v-else-if="col.format === 'status'">
                    <span :class="`badge badge--${item[col.key]}`">{{
                      item[col.key]
                    }}</span>
                  </span>
                  <span v-else-if="col.format === 'dimensions'">
                    {{
                      item.widthCm && item.heightCm
                        ? `${item.widthCm}×${item.heightCm}`
                        : "—"
                    }}
                  </span>
                  <span v-else>{{ item[col.key] ?? "—" }}</span>
                </td>
                <td class="actions-cell">
                  <AzioniRiga>
                    <button
                      class="icons-button"
                      @click="goToEditItem(item.id)"
                      title="Modifica articolo"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button
                      class="icons-button icons-button--danger"
                      @click="openArchiveModal(item)"
                      title="Archivia articolo"
                    >
                      <i class="fa-solid fa-trash-can"></i>
                    </button>
                  </AzioniRiga>
                </td>
              </tr>
            </template>
            <tr v-if="items.length === 0">
              <td :colspan="columns.length + 1" class="no-result">
                Nessun articolo trovato.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-left"></div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal archiviazione -->
    <VueFinalModal
      v-model="showArchiveModal"
      :clickToClose="!archiving"
      :escToClose="!archiving"
      classes="documents-modal"
      content-class="confirm-modal-content modal--archive"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="closeArchiveModal"
          :disabled="archiving"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Archivia articolo</h3>
        <p class="modal__subtitle">
          Archiviare
          <strong>{{ selectedItem?.itemName || selectedItem?.brand }}</strong
          >?
        </p>
        <div class="modal-actions">
          <button
            class="secondary-btn"
            @click="closeArchiveModal"
            :disabled="archiving"
          >
            Annulla
          </button>
          <button
            class="primary-btn confirm-btn"
            @click="confirmArchive"
            :disabled="archiving"
          >
            <span v-if="archiving">Archivia…</span>
            <span v-else>Archivia</span>
          </button>
        </div>
      </template>
    </VueFinalModal>

    <DettaglioModal
      v-model:show="dettaglio.show"
      :titolo="dettaglio.titolo"
      :gruppi="dettaglio.gruppi"
      :loading="dettaglio.loading"
    />
  </main>
  <router-view />
</template>

<script>
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatPriceToPrint } from "../utilities/formatting";
import DettaglioModal from "@/components/DettaglioModal.vue";
import AzioniRiga from "@/components/AzioniRiga.vue";
import dettaglioRiga from "@/mixins/dettaglioRiga";
import StatusSelect from "@/components/StatusSelect.vue";
import {
  FILTRI_VUOTI,
  cambiaFiltro,
  serieDisponibile,
  filtriAttivi,
  opzioniFiltro,
  parametriFiltri,
  valoriPresenti,
} from "@/utilities/filtriListino";

// voci dei filtri della categoria: serieCategoria dice se il filtro Serie va
// mostrato, serie sono quelle del brand scelto
const VALORI_VUOTI = () => ({
  brand: [],
  serieCategoria: [],
  serie: [],
  tipologia: [],
});

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

// [sc-48893] prio: 2 = nascosta sotto i 1024px, 3 = sotto i 1200px;
// senza prio sempre visibile. Le colonne nascoste restano nel dettaglio.
const COLUMNS = {
  area: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true, prio: 2 },
    { key: "serie", label: "Serie", truncate: true, prio: 2 },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "formatLabel", label: "Formato", prio: 2 },
    { key: "surface", label: "Superficie", prio: 3 },
    { key: "piecesPerBox", label: "Pz/sc", prio: 3 },
    { key: "sqmPerBox", label: "M²/sc", prio: 3 },
    { key: "price", label: "Prezzo listino", format: "price", prio: 2 },
    { key: "priceDiscounted", label: "Prezzo scontato", format: "price" },
  ],
  piece: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true, prio: 2 },
    { key: "serie", label: "Serie", truncate: true, prio: 2 },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "fixtureType", label: "Tipo", prio: 2 },
    { key: "fixturePosition", label: "Posizione", prio: 3 },
    { key: "finish", label: "Finitura", truncate: true, prio: 3 },
    { key: "price", label: "Prezzo listino", format: "price", prio: 2 },
    { key: "priceDiscounted", label: "Prezzo scontato", format: "price" },
  ],
  linear: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true, prio: 2 },
    { key: "serie", label: "Serie", truncate: true, prio: 2 },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "lengthMm", label: "Lung. mm", prio: 2 },
    { key: "mlPerBox", label: "ML/sc", prio: 3 },
    { key: "material", label: "Materiale", truncate: true, prio: 3 },
    { key: "price", label: "Prezzo listino", format: "price", prio: 2 },
    { key: "priceDiscounted", label: "Prezzo scontato", format: "price" },
  ],
  furniture: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true, prio: 2 },
    { key: "serie", label: "Serie", truncate: true, prio: 2 },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "dimensions", label: "Dim.", format: "dimensions", prio: 2 },
    { key: "material", label: "Materiale", truncate: true, prio: 3 },
    { key: "finish", label: "Finitura", truncate: true, prio: 3 },
    { key: "price", label: "Prezzo listino", format: "price", prio: 2 },
    { key: "priceDiscounted", label: "Prezzo scontato", format: "price" },
  ],
  generic: [
    { key: "itemCode", label: "Codice", truncate: true },
    { key: "brand", label: "Brand", truncate: true, prio: 2 },
    { key: "itemName", label: "Nome", truncate: true },
    { key: "notes", label: "Descrizione", truncate: true, prio: 3 },
    { key: "price", label: "Prezzo listino", format: "price", prio: 2 },
    { key: "priceDiscounted", label: "Prezzo scontato", format: "price" },
  ],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

export default {
  name: "ListiniCategoryPage",
  components: { DettaglioModal, AzioniRiga, StatusSelect },
  mixins: [dettaglioRiga],
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchTerm: "",
      filtri: { ...FILTRI_VUOTI },
      valori: VALORI_VUOTI(),
      selectedItem: null,
      showArchiveModal: false,
      archiving: false,
      refreshing: false,
      refreshed: false,
    };
  },
  computed: {
    ...mapGetters({
      allItems: "items/list",
      pagination: "items/pagination",
    }),
    items() {
      return this.allItems.filter((i) => i.category === this.category);
    },
    columns() {
      return COLUMNS[getCategoryGroup(this.category)];
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    ...mapActions(["fetchItems"]),
    formatPriceToPrint,

    opzioniFiltro,
    valoriPresenti,
    filtriAttivi,

    // [sc-48920] voci di un filtro da item/field-values; un errore lascia il
    // filtro vuoto (nascosto) e non blocca l'elenco
    async valoriCampo(field, brand = "") {
      try {
        const { data } = await this.$axios.get("item/field-values", {
          params: {
            field,
            category: this.category,
            ...(brand ? { brand } : {}),
          },
        });
        return data.values || [];
      } catch {
        return [];
      }
    },
    async caricaValori() {
      const [brand, serieCategoria, tipologia] = await Promise.all([
        this.valoriCampo("brand"),
        this.valoriCampo("serie"),
        this.valoriCampo("tipologia"),
      ]);
      this.valori = { ...this.valori, brand, serieCategoria, tipologia };
      await this.caricaSerie();
    },
    // serie del brand scelto; senza brand il filtro Serie usa quelle della categoria
    async caricaSerie() {
      this.valori.serie = this.filtri.brand
        ? await this.valoriCampo("serie", this.filtri.brand)
        : [];
    },
    imposta(campo, valore) {
      const brandPrima = this.filtri.brand;
      this.filtri = cambiaFiltro(this.filtri, campo, valore);
      if (this.filtri.brand === brandPrima) return this.loadItems(1);
      // brand cambiato: le serie proposte diventano le sue, e una serie che
      // il nuovo brand non ha si azzera (l'elenco sarebbe sempre vuoto)
      return this.caricaSerie().then(() => {
        const serie = serieDisponibile(
          this.filtri.serie,
          this.filtri.brand ? this.valori.serie : this.valori.serieCategoria
        );
        this.filtri = { ...this.filtri, serie };
        return this.loadItems(1);
      });
    },
    azzeraFiltri() {
      this.filtri = { ...FILTRI_VUOTI };
      this.valori.serie = [];
      // con una ricerca scritta ricarica il watcher di searchTerm
      if (this.searchTerm) this.searchTerm = "";
      else this.loadItems(1);
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await Promise.all([
          this.loadItems(this.pagination.currentPage),
          this.caricaValori(),
        ]);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    openArchiveModal(item) {
      this.selectedItem = item;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedItem = null;
    },

    goToEditItem(itemId) {
      this.$router.push({
        name: "listini-edit-item",
        params: { category: this.category, itemId },
      });
    },

    async onSearch() {
      await this.loadItems(1);
    },
    // il watcher di searchTerm ricarica l'elenco, con i filtri scelti
    onResetSearch() {
      this.searchTerm = "";
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadItems(page);
    },
    async loadItems(page = 1) {
      try {
        await this.fetchItems({
          page,
          limit: 8,
          category: this.category,
          search: this.searchTerm,
          ...parametriFiltri(this.filtri),
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Errore durante il caricamento degli articoli."
        );
      }
    },

    async confirmArchive() {
      if (!this.selectedItem) return;
      try {
        this.archiving = true;
        await this.$axios.patch(
          `item/archive/${this.selectedItem.id}`,
          JSON.stringify({ archived: true })
        );
        this.$toast.success("Articolo archiviato con successo.");
        this.showArchiveModal = false;
        await this.loadItems(this.pagination.currentPage);
        if (
          this.pagination.currentPage > this.pagination.totalPages &&
          this.pagination.totalPages >= 1
        ) {
          await this.loadItems(this.pagination.totalPages);
        }
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'articolo."
        );
      } finally {
        this.archiving = false;
        this.selectedItem = null;
      }
    },
  },
  mounted() {
    if (this.category) {
      this.loadItems(1);
      this.caricaValori();
    }
  },
  watch: {
    category() {
      this.searchTerm = "";
      this.filtri = { ...FILTRI_VUOTI };
      this.valori = VALORI_VUOTI();
      this.loadItems(1);
      this.caricaValori();
    },
    searchTerm() {
      this.onSearch();
    },
  },
};
</script>

<style src="@/css/StockPage.css" />
